import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { GetMasterStructureMappings, GetMasterStructures } from '../graphql/masterStructures';
import {
  buildMasterAccountIndex,
  extractMasterLeafOptions,
  masterAccountRef,
} from '../utils/masterStructureHelpers';

/** Reintentos automáticos ante un fallo de carga, con espera creciente. */
const MAX_REINTENTOS = 3;
const REINTENTO_MS = 1500;

const MasterStructuresContext = createContext({
  structures: [],
  structuresByType: {},
  masterAccountIndex: new Map(),
  optionsByStructureType: {},
  mappings: [],
  mappingBySourceKey: new Map(),
  assignedCountByMasterKey: new Map(),
  loading: false,
  error: null,
  refetchStructures: () => {},
  refetchMappings: () => {},
});

export const MasterStructuresProvider = ({ children }) => {
  const { selectedCompany } = useSelector((state) => state.companyList);
  const companyId = selectedCompany?.id;
  const hasMounted = useRef(false);
  const reintentos = useRef(0);

  const structuresQuery = GetMasterStructures({ companyId });
  const mappingsQuery = GetMasterStructureMappings({
    companyId,
    sourceType: 'ACCOUNT_CHART',
  });

  // Refetch al cambiar de empresa (en el mount urql ya dispara solo)
  useEffect(() => {
    if (companyId && hasMounted.current) {
      structuresQuery.reexecuteQuery({ requestPolicy: 'network-only' });
      mappingsQuery.reexecuteQuery({ requestPolicy: 'network-only' });
    }
    hasMounted.current = true;
    reintentos.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /*
    Un solo fallo de red deja al contexto en error para TODA la sesión: urql no
    reintenta y el proveedor vive en el Layout, así que sin esto un deploy a
    mitad de carga deja los selectores de cuenta maestra muertos —con el cartel
    "recargue la página"— y las fórmulas mostrando claves en vez de nombres,
    hasta que el usuario efectivamente recarga.
  */
  useEffect(() => {
    const fallo = structuresQuery.error || mappingsQuery.error;
    if (!companyId || !fallo || reintentos.current >= MAX_REINTENTOS) return;
    const espera = setTimeout(() => {
      reintentos.current += 1;
      if (structuresQuery.error) {
        structuresQuery.reexecuteQuery({ requestPolicy: 'network-only' });
      }
      if (mappingsQuery.error) {
        mappingsQuery.reexecuteQuery({ requestPolicy: 'network-only' });
      }
    }, REINTENTO_MS * (reintentos.current + 1));
    return () => clearTimeout(espera);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, structuresQuery.error, mappingsQuery.error]);

  const value = useMemo(() => {
    const structures = structuresQuery.data?.masterStructures || [];
    const mappings = mappingsQuery.data?.masterStructureMappings || [];

    const structuresByType = structures.reduce((acc, structure) => {
      acc[structure.type] = structure;
      return acc;
    }, {});

    // `${structureType}::${key}` → { structureType, key, name, label, path }
    const masterAccountIndex = buildMasterAccountIndex(structures);

    // Opciones del selector calculadas UNA vez por estructura: el Plan de Cuentas
    // puede renderizar ~125 dropdowns a la vez y cada uno aplanaría el árbol.
    const optionsByStructureType = structures.reduce((acc, structure) => {
      acc[structure.type] = extractMasterLeafOptions(structure);
      return acc;
    }, {});

    // Una cuenta de origen tiene a lo más una asignación (1:1 mientras la
    // ponderación esté apagada).
    const mappingBySourceKey = new Map();
    const assignedCountByMasterKey = new Map();
    mappings.forEach((mapping) => {
      mappingBySourceKey.set(mapping.sourceKey, mapping);
      // Se cuenta por (estructura, key): la key es local a cada estructura.
      const ref = masterAccountRef(mapping.structureType, mapping.masterKey);
      assignedCountByMasterKey.set(ref, (assignedCountByMasterKey.get(ref) || 0) + 1);
    });

    return {
      structures,
      structuresByType,
      masterAccountIndex,
      optionsByStructureType,
      mappings,
      mappingBySourceKey,
      assignedCountByMasterKey,
      loading: structuresQuery.fetching || mappingsQuery.fetching,
      error: structuresQuery.error || mappingsQuery.error,
      refetchStructures: structuresQuery.reexecuteQuery,
      refetchMappings: mappingsQuery.reexecuteQuery,
    };
  }, [
    structuresQuery.data,
    structuresQuery.fetching,
    structuresQuery.error,
    structuresQuery.reexecuteQuery,
    mappingsQuery.data,
    mappingsQuery.fetching,
    mappingsQuery.error,
    mappingsQuery.reexecuteQuery,
  ]);

  return (
    <MasterStructuresContext.Provider value={value}>
      {children}
    </MasterStructuresContext.Provider>
  );
};

export const useMasterStructures = () => {
  const context = useContext(MasterStructuresContext);
  if (!context) {
    throw new Error('useMasterStructures must be used within MasterStructuresProvider');
  }
  return context;
};
